import type React from 'react';
import { Button, Col, Input, Row, Space, Spin, Tag, Alert, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import {
  CARD_PREFIX_TO_BRAND_CODE,
  getCardBrandByCode,
  getCardTypeByCode,
  LinkedCard,
  LinkingResult,
  useGetCodePack,
  useLinkCardCodes,
  useUnlink,
} from 'services/InterbankService';
import { useFindPackagesByExternalCode } from 'services/PackageService';

const LinkedCodepackComponent = ({ codepack }: { codepack: string }) => {
  const { data: shipments, isLoading } = useFindPackagesByExternalCode({
    externalCode: codepack,
    size: 10,
    page: 0,
  });

  const foundShipment = useMemo(() => {
    return shipments?.content.find(
      (shipment) =>
        shipment.externalCode === codepack && shipment.status !== 21,
    );
  }, [shipments, codepack]);

  const link = useMemo(() => {
    if (!foundShipment) {
      return null;
    }

    return `${window.location.origin}/packages/id/${foundShipment.id}`;
  }, [foundShipment]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '20vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!link) {
    return null;
  }

  if (!foundShipment) {
    return null;
  }

  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      Ver envio {foundShipment.externalCode}
    </a>
  );
};

interface FormValues {
  [key: string]: string;
}

interface InterbankProps {
  packet: {
    externalCode: string;
  };
}

interface ValidationStatus {
  status: 'NONE' | 'VALID' | 'SUCCESS' | 'INVALID';
  message: string;
}

const getCardPrefix = (cardCode: string): string => {
  if (!cardCode || cardCode.length < 2) {
    return '';
  }
  return cardCode.substring(0, 2).toUpperCase();
};

const getExpectedBrandCodeFromCardCode = (cardCode: string): string | null => {
  const prefix = getCardPrefix(cardCode);
  return CARD_PREFIX_TO_BRAND_CODE[prefix] || null;
};

interface CardValidationResult {
  isValid: boolean;
  error: string | null;
}

const validateCardCode = (
  cardCode: string,
  expectedCardType: string,
): CardValidationResult => {
  // Validaciones básicas
  if (!cardCode || cardCode.trim() === '') {
    return {
      isValid: false,
      error: 'El código de tarjeta es requerido',
    };
  }

  if (cardCode.length < 2) {
    return {
      isValid: false,
      error: 'El código de tarjeta debe tener al menos 2 caracteres',
    };
  }

  // Validar formato básico (letras seguidas de números)
  const formatRegex = /^[A-Za-z]{2}[0-9]+$/;
  if (!formatRegex.test(cardCode)) {
    return {
      isValid: false,
      error: 'El código debe comenzar con 2 letras seguidas de números',
    };
  }

  // Obtener el código de marca esperado basado en el prefijo
  const expectedBrandFromPrefix = getExpectedBrandCodeFromCardCode(cardCode);

  if (!expectedBrandFromPrefix) {
    const prefix = getCardPrefix(cardCode);
    return {
      isValid: false,
      error: `Prefijo "${prefix}" no reconocido. Prefijos válidos: ${Object.keys(CARD_PREFIX_TO_BRAND_CODE).join(', ')}`,
    };
  }

  // Validar que el prefijo coincida con la marca esperada
  if (expectedBrandFromPrefix !== expectedCardType) {
    const prefix = getCardPrefix(cardCode);
    const expectedBrandName =
      getCardTypeByCode(expectedCardType)?.name || expectedCardType;
    const actualBrandName =
      getCardTypeByCode(expectedBrandFromPrefix)?.name ||
      expectedBrandFromPrefix;

    return {
      isValid: false,
      error: `El prefijo "${prefix}" corresponde a ${actualBrandName}, pero se esperaba ${expectedBrandName}`,
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

const createCardCodeValidator = (expectedCardType: string) => {
  return (value: string): string | boolean => {
    const result = validateCardCode(value, expectedCardType);
    return result.isValid || result.error || false;
  };
};

const getCardPrefixHelp = (cardType: string): string => {
  const brandInfo = getCardTypeByCode(cardType);
  if (!brandInfo) return '';

  // Mapeo inverso para encontrar el prefijo esperado
  const expectedPrefix = Object.keys(CARD_PREFIX_TO_BRAND_CODE).find(
    (prefix) => CARD_PREFIX_TO_BRAND_CODE[prefix] === cardType,
  );

  return expectedPrefix
    ? `Debe comenzar con "${expectedPrefix}" para ${brandInfo.name}`
    : '';
};

// Main component
export default function Interbank({ packet }: InterbankProps) {
  const [showResults, setShowResults] = useState<boolean>(false);

  const { data: codePack, isLoading: loadingCodePack } = useGetCodePack(
    packet.externalCode,
  );

  const {
    mutate: validateCardCodes,
    isPending: isValidating,
    data: validationResults,
    reset: resetLink,
  } = useLinkCardCodes();

  const {
    mutate: unlinkCardCodes,
    isPending: isUnlinkingCardCodes,
    reset: resetUnlink,
  } = useUnlink();

  const {
    control,
    getValues,
    watch,
    reset: resetForm,
    formState: { errors, isValid: isFormValid },
  } = useForm<FormValues>({
    mode: 'onChange',
  });

  const isCodepackLinked = useMemo(() => {
    if (!codePack) return false;

    return codePack.LinkedCards.some((links) => Boolean(links.cardNumber));
  }, [codePack]);

  useEffect(() => {
    if (isCodepackLinked && codePack) {
      const obj = codePack.LinkedCards.reduce(
        (acc: FormValues, requestCode) => {
          acc[`cardCode_${requestCode.requestId}`] =
            requestCode.cardNumber || '';
          return acc;
        },
        {},
      );

      resetForm(obj);
    }
  }, [isCodepackLinked, codePack, resetForm]);

  const watchedValues = watch();

  const isFormComplete = useMemo((): boolean => {
    if (!codePack) return false;

    if (!isFormValid) return false;

    return codePack.LinkedCards.every((requestCode: LinkedCard) =>
      watchedValues[`cardCode_${requestCode.requestId}`]?.trim(),
    );
  }, [codePack, watchedValues, isFormValid]);

  const isValid = useMemo((): boolean => {
    return Boolean(
      validationResults?.linkingResults.every(
        (r: LinkingResult) => r.result === 'OK',
      ) && isFormComplete,
    );
  }, [validationResults, isFormComplete]);

  const getValidationRules = (requestCode: LinkedCard) => ({
    required: 'El código de tarjeta es requerido',
    validate: createCardCodeValidator(requestCode.cardType),
  });

  const handleResetResults = () => {
    resetLink();
    resetUnlink();
    setShowResults(false);
  };

  const handleReset = (): void => {
    resetForm();
    handleResetResults();
  };

  const onSubmit = (): void => {
    const values = getValues();

    if (!codePack) return;

    const cardCodesToValidate = codePack.LinkedCards.map(
      (requestCode: LinkedCard) => ({
        requestCodeId: requestCode.requestId,
        cardCode: values[`cardCode_${requestCode.requestId}`],
        cardType: requestCode.cardType,
        cardBrand: requestCode.cardBrand,
      }),
    );

    handleResetResults();

    validateCardCodes(
      {
        codPack: codePack.codPack,
        LinkingProposal: cardCodesToValidate.map((code) => ({
          cardNumber: code.cardCode,
          requestId: code.requestCodeId,
        })),
      },
      {
        onSuccess: () => {
          setShowResults(true);
        },
      },
    );
  };

  const getValidationStatus = (
    requestCodeId: string,
  ): ValidationStatus | null => {
    const result = validationResults?.linkingResults.find(
      (r: LinkingResult) => r.requestId === requestCodeId,
    );
    if (!result) return null;

    let status: 'NONE' | 'VALID' | 'SUCCESS' | 'INVALID' = 'NONE';

    if (result.result === 'OK') {
      if (!isValid) {
        status = 'VALID';
      } else {
        status = 'SUCCESS';
      }
    } else if (result.result === 'NOK') {
      status = 'INVALID';
    }

    return {
      status,
      message: result.reason,
    };
  };

  const getValidationMessage = (requestCodeId: string): string | null => {
    const result = validationResults?.linkingResults.find(
      (r: LinkingResult) => r.requestId === requestCodeId,
    );
    if (!result) return null;
    return result.result === 'OK' ? 'Código válido' : result.reason;
  };

  const getFieldError = (requestCodeId: string): string | undefined => {
    const fieldName = `cardCode_${requestCodeId}`;
    return errors[fieldName]?.message;
  };

  const getInputStatus = (requestCodeId: string): 'error' | undefined => {
    const fieldError = getFieldError(requestCodeId);
    const validationStatus = getValidationStatus(requestCodeId);

    if (fieldError) return 'error';
    if (validationStatus?.status === 'INVALID') return 'error';
    return undefined;
  };

  const getHelpText = (requestCodeId: string): string | undefined => {
    const fieldError = getFieldError(requestCodeId);
    if (fieldError) return fieldError;
    if (showResults) return getValidationMessage(requestCodeId) || undefined;
    return undefined;
  };

  const getPlaceholderForCard = (cardBrand: string): string => {
    const prefix = Object.keys(CARD_PREFIX_TO_BRAND_CODE).find(
      (p) => CARD_PREFIX_TO_BRAND_CODE[p] === cardBrand,
    );
    return prefix ? `Ej: ${prefix}2501234567` : 'Ingrese código de tarjeta';
  };

  const handleUnlinkCodes = () => {
    handleReset();
    unlinkCardCodes({ trackCode: packet.externalCode });
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      render: (_: unknown, __: unknown, index: number): number => index + 1,
    },
    {
      title: 'Código de Solicitud',
      dataIndex: 'requestId',
      key: 'requestId',
    },
    {
      title: 'Tipo de Tarjeta',
      dataIndex: 'cardType',
      key: 'cardType',
      render: (cardType: string): React.JSX.Element => {
        const typeInfo = getCardTypeByCode(cardType);
        return <Tag color="blue">{typeInfo?.name || cardType}</Tag>;
      },
    },
    {
      title: 'Marca de Tarjeta',
      dataIndex: 'cardBrand',
      key: 'cardBrand',
      render: (cardBrand: string): React.JSX.Element => {
        const brandInfo = getCardBrandByCode(cardBrand);
        return <Tag color="green">{brandInfo?.name || cardBrand}</Tag>;
      },
    },
    {
      title: 'Código de Tarjeta',
      dataIndex: 'requestId',
      key: 'cardCode',
      render: (id: string, record: LinkedCard): React.JSX.Element => {
        const status = getInputStatus(id);
        const helpText = getHelpText(id);
        const prefixHelp = getCardPrefixHelp(record.cardType);

        if (isCodepackLinked) {
          return <p>{record.cardNumber}</p>;
        }

        return (
          <div>
            <Controller
              name={`cardCode_${id}`}
              control={control}
              rules={getValidationRules(record)}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={getPlaceholderForCard(record.cardBrand)}
                  disabled={isValidating}
                  status={status}
                  suffix={
                    showResults &&
                    getValidationStatus(id) &&
                    (getValidationStatus(id)?.status === 'SUCCESS' ||
                    getValidationStatus(id)?.status === 'VALID' ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    ))
                  }
                />
              )}
            />
            {prefixHelp && !helpText && (
              <div
                style={{ color: '#8c8c8c', fontSize: '11px', marginTop: '2px' }}
              >
                {prefixHelp}
              </div>
            )}
            {helpText && (
              <div
                style={{
                  color: status === 'error' ? '#ff4d4f' : '#52c41a',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                {helpText}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loadingCodePack) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '20vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!codePack) {
    return (
      <Alert
        message="No se encontró el paquete de códigos"
        description="No hay códigos de solicitud asociados a este envío."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Table
        columns={columns}
        dataSource={codePack.LinkedCards}
        rowKey="requestId"
        pagination={false}
        bordered
        size="middle"
        scroll={{ x: 800 }}
      />

      {showResults && (
        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <Alert
              message={isValid ? 'Vinculación exitosa' : 'Errores encontrados'}
              description={
                isValid ? (
                  'Todos los códigos de tarjeta fueron vinculados correctamente'
                ) : (
                  <div>
                    <p>Se encontraron errores en los siguientes códigos:</p>
                    <ul>
                      {validationResults?.linkingResults
                        .filter((r: LinkingResult) => r.result === 'NOK')
                        .map((result: LinkingResult) => (
                          <li key={result.requestId}>
                            <p>{result.cardNumber}</p>
                            <p>{result.reason}</p>
                            <div>
                              <LinkedCodepackComponent
                                codepack={result.linkedCodPack || ''}
                              />
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                )
              }
              type={
                validationResults?.linkingResults.every(
                  (r: LinkingResult) => r.result === 'OK',
                )
                  ? 'success'
                  : 'error'
              }
              showIcon
            />
          </Col>
        </Row>
      )}

      <Row justify="end" style={{ marginTop: '24px' }}>
        <Space>
          {showResults && (
            <Button onClick={handleReset} disabled={isValidating} size="large">
              Limpiar Resultados
            </Button>
          )}

          {isCodepackLinked ? (
            <Button
              danger
              onClick={handleUnlinkCodes}
              loading={isUnlinkingCardCodes}
              disabled={isUnlinkingCardCodes}
              size="large"
            >
              {isUnlinkingCardCodes ? '' : 'Desvincular codigos'}
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={onSubmit}
              loading={isValidating}
              disabled={!isFormComplete || isValidating}
              size="large"
            >
              {isValidating ? 'Validando...' : 'Vincular códigos'}
            </Button>
          )}
        </Space>
      </Row>
    </div>
  );
}
